/**
 * DiagramLegend Component (Issue #156)
 *
 * Floating legend overlay that displays symbol meanings.
 * Can be toggled on/off and dragged to different positions.
 */

import { useState, useMemo } from 'react';
import { diagramDescriptions, type LegendItem } from '@veodiagram/shared';

interface DiagramLegendProps {
  diagramType?: string | null;
  customLegend?: LegendItem[];
  isVisible: boolean;
  onClose: () => void;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export function DiagramLegend({
  diagramType,
  customLegend,
  isVisible,
  onClose,
  position = 'bottom-right',
}: DiagramLegendProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Get legend items from static descriptions or custom
  const legendItems = useMemo(() => {
    if (customLegend && customLegend.length > 0) {
      return customLegend;
    }

    if (!diagramType) return [];

    // Convert diagram type to key format
    const key = diagramType.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    const description = diagramDescriptions[key] || diagramDescriptions[diagramType];

    return description?.defaultLegend || [];
  }, [diagramType, customLegend]);

  if (!isVisible || legendItems.length === 0) return null;

  // Position classes
  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  return (
    <div
      className={`absolute ${positionClasses[position]} z-30 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden min-w-[180px] max-w-[280px]`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-700/50 cursor-pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Legend
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsCollapsed(!isCollapsed);
            }}
            className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
          >
            <svg
              className={`w-4 h-4 text-slate-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-3 space-y-2">
          {legendItems.map((item, index) => (
            <div key={index} className="flex items-start gap-2">
              <span className="text-base leading-none mt-0.5 flex-shrink-0">{item.symbol}</span>
              <div className="text-xs">
                <span className="text-slate-700 dark:text-slate-300">{item.meaning}</span>
                {item.example && (
                  <span className="text-slate-400 dark:text-slate-500 block mt-0.5">
                    e.g., {item.example}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DiagramLegend;
